import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { notifyStaffAuthChanged } from '../../auth/appAuth';
import { useAuth } from '../../context/AuthContext';
import ImpersonationAuthScreen from '../components/ImpersonationAuthScreen';
import { claimImpersonationTicket } from '../api/claimImpersonationApi';
import {
  applyImpersonationSessionToken,
  clearImpersonationHandoff,
  consumeImpersonationHandoff,
} from '../utils/impersonationHandoff';
import {
  clearImpersonationEntering,
  getImpersonationTargetLabel,
} from '../utils/impersonationSession';
import {
  clearImpersonationLoaderStarted,
  getImpersonationLoaderRemaining,
} from '../exit/impersonationExit';

const AUTH_WAIT_TIMEOUT_MS = 25000;

const STAGE_MESSAGES = [
  { at: 0, message: 'Authenticating…' },
  { at: 800, message: 'Signing into this account…' },
  { at: 1800, message: 'Preparing workspace…' },
];

function readHandoffOnFirstRender(slotRef) {
  if (slotRef.current) return slotRef.current;
  try {
    const handoff = consumeImpersonationHandoff();
    slotRef.current = { ok: Boolean(handoff), handoff, error: null };
  } catch (error) {
    slotRef.current = {
      ok: false,
      handoff: null,
      error: error?.message || 'Failed to start impersonation session.',
    };
  }
  return slotRef.current;
}

const AppImpersonateLandingPage = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const handoffSlotRef = useRef(null);
  const initial = readHandoffOnFirstRender(handoffSlotRef);
  const [error, setError] = useState(initial.error);
  const [stageIndex, setStageIndex] = useState(0);
  const [minElapsed, setMinElapsed] = useState(false);

  const targetUserId = initial.handoff?.userId || null;
  const targetLabel = initial.handoff?.target?.name
    || initial.handoff?.target?.email
    || getImpersonationTargetLabel();
  const companyName = initial.handoff?.target?.companyName || null;

  const hydrated = Boolean(
    targetUserId &&
    user?.uid === targetUserId &&
    !authLoading &&
    (user.companyId || user.role)
  );

  useEffect(() => {
    const timer = window.setTimeout(
      () => setMinElapsed(true),
      getImpersonationLoaderRemaining()
    );
    return () => window.clearTimeout(timer);
  }, []);

  useEffect(() => {
    let cancelled = false;

    const claimAndAdopt = async () => {
      if (!initial.ok || !initial.handoff) return;
      if (initial.handoff.alreadyClaimed) {
        notifyStaffAuthChanged();
        return;
      }
      if (!initial.handoff.ticket) {
        setError('No impersonation ticket was provided.');
        return;
      }

      try {
        const claimed = await claimImpersonationTicket(initial.handoff.ticket);
        if (cancelled) return;
        applyImpersonationSessionToken({
          token: claimed.token,
          userId: initial.handoff.userId,
          companyId: claimed.target?.companyId || initial.handoff.companyId,
          target: claimed.target || initial.handoff.target,
        });
        notifyStaffAuthChanged();
      } catch (err) {
        setError(err?.message || 'Failed to start impersonation session.');
      }
    };

    void claimAndAdopt();
    return () => {
      cancelled = true;
    };
  }, [initial]);

  useEffect(() => {
    if (!initial.ok || error) return undefined;
    const timers = STAGE_MESSAGES.slice(1).map((stage, idx) => (
      window.setTimeout(() => setStageIndex(idx + 1), stage.at)
    ));
    return () => timers.forEach((id) => window.clearTimeout(id));
  }, [error, initial.ok]);

  useEffect(() => {
    if (!hydrated || !minElapsed) return undefined;
    clearImpersonationEntering();
    clearImpersonationHandoff();
    clearImpersonationLoaderStarted();
    navigate('/app/overview', { replace: true });
    return undefined;
  }, [hydrated, minElapsed, navigate]);

  useEffect(() => {
    if (!initial.ok || hydrated || error) return undefined;
    const timer = window.setTimeout(() => {
      setError('This account is taking too long to load. Please try again.');
    }, AUTH_WAIT_TIMEOUT_MS);
    return () => window.clearTimeout(timer);
  }, [error, hydrated, initial.ok]);

  const message = useMemo(() => {
    if (stageIndex === 0 && targetLabel) {
      return `Signing in as ${targetLabel}…`;
    }
    return STAGE_MESSAGES[stageIndex]?.message || STAGE_MESSAGES[0].message;
  }, [stageIndex, targetLabel]);

  const detail = companyName && stageIndex > 0
    ? companyName
    : 'Secure admin access · skipping customer 2FA';

  if (error || !initial.ok) {
    return (
      <ImpersonationAuthScreen
        status="error"
        error={error || 'No impersonation session was provided.'}
        onRetry={() => window.location.reload()}
      />
    );
  }

  return (
    <ImpersonationAuthScreen
      status="loading"
      message={message}
      detail={detail}
    />
  );
};

export default AppImpersonateLandingPage;
